import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  activityLogsAPI,
  type ActivityLogEntry,
  type ActivityLogListParams,
} from "@/lib/api";
import { toast } from "sonner";

const MODULES: { value: string; label: string }[] = [
  { value: "auth", label: "Auth" },
  { value: "proveedores", label: "Proveedores" },
  { value: "pedidos", label: "Pedidos" },
  { value: "clientes", label: "Clientes" },
  { value: "ventas", label: "Ventas" },
  { value: "presupuestos", label: "Presupuestos" },
  { value: "recibos", label: "Recibos" },
  { value: "remitos", label: "Remitos Vtas" },
  { value: "stock", label: "Stock" },
  { value: "cheques", label: "Cheques" },
  { value: "caja", label: "Caja & Bancos" },
  { value: "fondos", label: "Fondos" },
  { value: "reportes", label: "Reportes" },
  { value: "opciones", label: "Opciones" },
];

const ACTION_LABELS: Record<string, string> = {
    login: "Inicio de sesión",
    logout: "Cierre de sesión",
    mfa_reset: "MFA reseteado",
    mfa_disable: "MFA deshabilitado",
  create: "Alta",
  update: "Edición",
  delete: "Baja",
  confirm: "Confirmación",
  generate: "Generación",
  restore: "Restore",
  import: "Importación",
  close: "Cierre",
  reconcile: "Reconciliación",
  validate: "Validación",
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] || action;
}

function moduleLabel(module: string): string {
  return MODULES.find((m) => m.value === module)?.label || module;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-AR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function ActivityLogsSection() {
  const [usuario, setUsuario] = useState("");
  const [modulo, setModulo] = useState("all");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [applied, setApplied] = useState<ActivityLogListParams>({});
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<ActivityLogEntry[]>([]);
  const [count, setCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ActivityLogEntry | null>(null);

  const query = useMemo<ActivityLogListParams>(
    () => ({
      page,
      page_size: 50,
      ...applied,
    }),
    [page, applied],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await activityLogsAPI.list(query);
      setRows(data.results);
      setCount(data.count);
      setHasNext(Boolean(data.next));
    } catch (error) {
      console.error("Error loading activity logs:", error);
      toast.error("No se pudo cargar el registro de actividad");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = () => {
    setPage(1);
    setApplied({
      usuario: usuario.trim() || undefined,
      modulo: modulo !== "all" ? modulo : undefined,
      fecha_desde: fechaDesde || undefined,
      fecha_hasta: fechaHasta || undefined,
    });
  };

  const exportCsv = async () => {
    try {
      const data = await activityLogsAPI.list({
        ...applied,
        page: 1,
        page_size: 200,
      });
      const header = [
        "Cuando",
        "Usuario",
        "Accion",
        "Modulo",
        "Metodo",
        "Ruta",
        "Objeto",
        "Resumen",
        "Resultado",
      ];
      const lines = data.results.map((row) =>
        [
          formatWhen(row.createdAt),
          row.actorUsername || "(desconocido)",
          actionLabel(row.action),
          moduleLabel(row.module),
          row.httpMethod,
          row.path,
          row.objectId || "",
          row.summary || "",
          row.success ? "ok" : "fail",
        ]
          .map((v) => csvEscape(String(v)))
          .join(","),
      );
      const blob = new Blob([[header.join(","), ...lines].join("\n")], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "registro-actividad.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el CSV");
    }
  };

  const pageCount = Math.max(1, Math.ceil(count / 50));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Acciones de negocio (altas, ediciones, bajas, login y logout). No hay
        borrado fila a fila; Reiniciar datos de pruebas (barra superior) también
        vacía este registro.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Label className="mb-1">Usuario</Label>
          <Input
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            placeholder="Nombre de usuario"
            className="h-9"
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
        </div>
        <div className="w-48">
          <Label className="mb-1">Módulo</Label>
          <Select value={modulo} onValueChange={setModulo}>
            <SelectTrigger className="h-9 w-full">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {MODULES.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1">Desde</Label>
          <DateInput
            value={fechaDesde}
            onChange={(ymd) => setFechaDesde(ymd)}
            className="h-9"
          />
        </div>
        <div>
          <Label className="mb-1">Hasta</Label>
          <DateInput
            value={fechaHasta}
            onChange={(ymd) => setFechaHasta(ymd)}
            className="h-9"
          />
        </div>
        <Button type="button" size="sm" onClick={applyFilters}>
          Filtrar
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void exportCsv()}
          className="flex items-center gap-1"
        >
          <Download className="h-3 w-3" />
          CSV
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground">
          Cargando registro de actividad...
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          No hay eventos con estos filtros
        </div>
      ) : (
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(2.5rem+14*2.25rem)] border border-input rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left">Cuándo</th>
                <th className="px-4 py-2 text-left">Quién</th>
                <th className="px-4 py-2 text-left">Acción</th>
                <th className="px-4 py-2 text-left">Módulo</th>
                <th className="px-4 py-2 text-left">Método</th>
                <th className="px-4 py-2 text-left">Objeto</th>
                <th className="px-4 py-2 text-left">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b hover:bg-muted/50 cursor-pointer"
                  onClick={() => setSelected(row)}
                >
                  <td className="px-4 py-2 whitespace-nowrap">
                    {formatWhen(row.createdAt)}
                  </td>
                  <td className="px-4 py-2">
                    {row.actorUsername || "(desconocido)"}
                  </td>
                  <td className="px-4 py-2">{actionLabel(row.action)}</td>
                  <td className="px-4 py-2">{moduleLabel(row.module)}</td>
                  <td className="px-4 py-2">{row.httpMethod}</td>
                  <td className="px-4 py-2 max-w-[16rem] truncate">
                    {row.summary || row.objectId || row.path}
                  </td>
                  <td className="px-4 py-2">
                    {row.success ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        ok
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        fail
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {count} evento{count === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <span>
            {page} / {pageCount}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasNext || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </Button>
        </div>
      </div>

      <Dialog open={selected != null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selected ? actionLabel(selected.action) : "Detalle"}
            </DialogTitle>
            <DialogDescription>
              {selected
                ? `${formatWhen(selected.createdAt)} · ${selected.actorUsername || "(desconocido)"}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-muted-foreground">Módulo</div>
                  <div>{moduleLabel(selected.module)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Método</div>
                  <div>{selected.httpMethod}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted-foreground">Ruta</div>
                  <div className="break-all font-mono text-xs">{selected.path}</div>
                </div>
                {selected.objectId ? (
                  <div>
                    <div className="text-muted-foreground">Id</div>
                    <div>{selected.objectId}</div>
                  </div>
                ) : null}
                {selected.summary ? (
                  <div>
                    <div className="text-muted-foreground">Resumen</div>
                    <div>{selected.summary}</div>
                  </div>
                ) : null}
              </div>
              {selected.payload && Object.keys(selected.payload).length > 0 ? (
                <div>
                  <div className="text-muted-foreground mb-1">Detalle</div>
                  <pre className="text-xs bg-muted rounded-lg p-3 overflow-x-auto">
                    {JSON.stringify(selected.payload, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
